import React, { createContext, useContext, useState, useEffect } from 'react';
import { defaultTranslations } from '../utils/translations';
import { api } from '../api/client';

const LanguageContext = createContext(null);

export const LANGUAGES = [
  { code: 'uz', name: 'O‘zbekcha', flag: '🇺🇿' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

export function LanguageProvider({ children }) {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('advokat_lang') || 'uz';
  });

  const [remoteTranslations, setRemoteTranslations] = useState({});

  useEffect(() => {
    // Attempt to load live translations from backend CMS
    api.get(`/cms/translations/${currentLang}`)
      .then((res) => {
        if (res?.success && res?.translations) {
          setRemoteTranslations(res.translations);
        }
      })
      .catch(() => {
        // Fallback to local default translations cleanly
      });
  }, [currentLang]);

  const setLanguage = (langCode) => {
    if (['uz', 'ru', 'en'].includes(langCode)) {
      setCurrentLang(langCode);
      localStorage.setItem('advokat_lang', langCode);
      document.documentElement.lang = langCode;
    }
  };

  /**
   * Translate key with multi-tiered fallback
   */
  const t = (key, fallback = '') => {
    // 1. Live CMS DB translation
    if (remoteTranslations[key]) {
      return remoteTranslations[key];
    }
    // 2. Local default dictionary for selected language
    if (defaultTranslations[currentLang]?.[key]) {
      return defaultTranslations[currentLang][key];
    }
    // 3. Fallback to English dictionary
    if (defaultTranslations.en?.[key]) {
      return defaultTranslations.en[key];
    }
    // 4. Return provided fallback string or key
    return fallback || key;
  };

  /**
   * Helper to pick localized field from an object (e.g. titleUz, titleRu, titleEn)
   */
  const localized = (obj, fieldBase) => {
    if (!obj) return '';
    const suffix = currentLang === 'uz' ? 'Uz' : currentLang === 'ru' ? 'Ru' : 'En';
    const field = `${fieldBase}${suffix}`;
    return obj[field] || obj[`${fieldBase}Uz`] || obj[`${fieldBase}En`] || '';
  };

  return (
    <LanguageContext.Provider value={{ currentLang, setLanguage, t, localized, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
